import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import {
	FunnelIcon,
	MagnifyingGlassIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import { Badge, StackableModal, DataTable } from '../../components/nextgen';

import useAsync from '../../hooks/useAsync';

import {
	getAuditTrails,
	getAuditEntityTypes,
	getAuditActions,
} from '../../api';

// Action type to badge variant mapping
const actionVariants = {
	CREATE: 'success',
	UPDATE: 'warning',
	DELETE: 'danger',
	PUBLISH: 'info',
	APPROVE: 'success',
	ARCHIVE: 'muted',
};

// Entity type display names
const entityTypeLabels = {
	asset: 'Asset',
	ticket: 'Ticket',
	sop: 'SOP',
	work_order: 'Work Order',
	contract: 'Contract',
	user: 'User',
	location: 'Location',
	contact: 'Contact',
	service_schedule: 'PM Schedule',
};

const AuditLog = () => {
	const location = useLocation();

	// Filters
	const [search, setSearch] = useState('');
	const [entityType, setEntityType] = useState('');
	const [entityId, setEntityId] = useState('');
	const [action, setAction] = useState('');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [showFilters, setShowFilters] = useState(false);
	const [page, setPage] = useState(1);
	const [selectedEntry, setSelectedEntry] = useState(null);

	// Read URL query parameters on mount
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const urlEntityType = params.get('entityType');
		const urlEntityId = params.get('entityId');

		if (urlEntityType) {
			setEntityType(urlEntityType);
			setShowFilters(true);
		}
		if (urlEntityId) {
			setEntityId(urlEntityId);
			setShowFilters(true);
		}
	}, [location.search]);

	// Data
	const {
		value: auditData,
		execute: refreshAuditTrails,
		status: loadingStatus,
	} = useAsync(getAuditTrails, { immediate: false });

	const { value: entityTypes } = useAsync(getAuditEntityTypes);
	const { value: actions } = useAsync(getAuditActions);

	// Fetch data when filters change
	useEffect(() => {
		const params = { page: page - 1, limit: 25 }; // API uses 0-based page
		if (search) params.search = search;
		if (entityType) params.entityType = entityType;
		if (entityId) params.entityId = entityId;
		if (action) params.action = action;
		if (startDate) params.startDate = startDate;
		if (endDate) params.endDate = endDate;

		refreshAuditTrails(params);
	}, [search, entityType, entityId, action, startDate, endDate, page]);

	const isLoading = loadingStatus === 'pending';

	// Render changes diff
	const renderChanges = (changes) => {
		if (!changes || Object.keys(changes).length === 0) {
			return <span className="text-gray-400 text-sm">No changes recorded</span>;
		}

		return (
			<div className="space-y-2">
				{Object.entries(changes).map(([field, change]) => (
					<div key={field} className="text-sm">
						<span className="font-medium text-gray-700">{field}:</span>
						<div className="ml-4 flex gap-2 items-center">
							<span className="text-red-600 line-through bg-red-50 px-1 rounded">
								{formatValue(change.from)}
							</span>
							<span className="text-gray-400">&rarr;</span>
							<span className="text-green-600 bg-green-50 px-1 rounded">
								{formatValue(change.to)}
							</span>
						</div>
					</div>
				))}
			</div>
		);
	};

	// Format value for display
	const formatValue = (value) => {
		if (value === null || value === undefined) return '(empty)';
		if (typeof value === 'boolean') return value ? 'true' : 'false';
		if (typeof value === 'object') return JSON.stringify(value);
		if (String(value).length > 50) return String(value).slice(0, 50) + '...';
		return String(value);
	};

	const columns = [
		{
			key: 'createdAt',
			header: 'Timestamp',
			width: '180px',
			sortable: false,
			render: (row) => (
				<div>
					<div className="text-gray-900">
						{moment(row.createdAt).format('MMM D, YYYY')}
					</div>
					<div className="text-gray-500">
						{moment(row.createdAt).format('h:mm:ss A')}
					</div>
				</div>
			),
		},
		{
			key: 'action',
			header: 'Action',
			width: '100px',
			sortable: false,
			render: (row) => (
				<Badge variant={actionVariants[row.action] || 'muted'}>
					{row.action}
				</Badge>
			),
		},
		{
			key: 'entityType',
			header: 'Entity Type',
			width: '130px',
			sortable: false,
			render: (row) => (
				<span className="text-gray-700">
					{entityTypeLabels[row.entityType] || row.entityType}
				</span>
			),
		},
		{
			key: 'entityName',
			header: 'Entity',
			sortable: false,
			render: (row) => (
				<div>
					<div className="text-gray-900 font-medium">
						{row.entityName || 'Unnamed'}
					</div>
					<div className="text-gray-500 text-xs font-mono">
						{row.entityId?.slice(0, 8)}...
					</div>
				</div>
			),
		},
		{
			key: 'actor',
			header: 'Actor',
			width: '200px',
			sortable: false,
			render: (row) => (
				<div>
					{row.actorName ? (
						<>
							<div className="text-gray-900">{row.actorName}</div>
							<div className="text-gray-500 text-xs">{row.actorEmail}</div>
						</>
					) : (
						<span className="text-gray-400">System</span>
					)}
				</div>
			),
		},
	];

	const activeFilterCount = [
		entityType,
		entityId,
		action,
		startDate,
		endDate,
	].filter(Boolean).length;

	return (
		<MainContent maxWidth={'w-full'}>
			<PageHeading divider={false}>Audit Log</PageHeading>

			{/* Search & Filters Bar */}
			<div className="bg-white border border-gray-200 rounded-lg mb-6">
				<div className="flex items-center gap-4 p-4">
					{/* Search Input */}
					<div className="flex-1 relative">
						<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
						<input
							type="text"
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
								setPage(1);
							}}
							placeholder="Search by entity name, actor..."
							className="w-full pl-10 pr-10 py-2.5 text-sm bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 placeholder:text-gray-400"
						/>
						{search && (
							<button
								onClick={() => {
									setSearch('');
									setPage(1);
								}}
								className="absolute right-3 top-1/2 -translate-y-1/2"
							>
								<XMarkIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
							</button>
						)}
					</div>

					{/* Filters Toggle */}
					<button
						onClick={() => setShowFilters(!showFilters)}
						className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border ${
							showFilters || activeFilterCount > 0
								? 'bg-brand-50 border-brand-200 text-brand-700'
								: 'border-gray-200 text-gray-600 hover:bg-gray-50'
						}`}
					>
						<FunnelIcon className="w-4 h-4" />
						Filters
						{activeFilterCount > 0 && (
							<span className="bg-brand-500 text-white text-xs px-1.5 py-0.5 rounded-full">
								{activeFilterCount}
							</span>
						)}
					</button>

					{/* Clear Filters */}
					{activeFilterCount > 0 && (
						<button
							onClick={() => {
								setEntityType('');
								setEntityId('');
								setAction('');
								setStartDate('');
								setEndDate('');
								setPage(1);
							}}
							className="text-sm text-gray-500 hover:text-gray-700"
						>
							Clear filters
						</button>
					)}
				</div>

				{/* Expandable Filters */}
				{showFilters && (
					<div className="border-t border-gray-200 p-4 space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Entity Type
								</label>
								<select
									value={entityType}
									onChange={(e) => {
										setEntityType(e.target.value);
										setPage(1);
									}}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-brand-500 focus:border-brand-500"
								>
									<option value="">All Types</option>
									{(entityTypes || []).map((type) => (
										<option key={type} value={type}>
											{entityTypeLabels[type] || type}
										</option>
									))}
								</select>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Entity ID
								</label>
								<input
									type="text"
									value={entityId}
									onChange={(e) => {
										setEntityId(e.target.value);
										setPage(1);
									}}
									placeholder="Filter by specific entity ID"
									className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-brand-500 focus:border-brand-500"
								/>
							</div>
						</div>
						<div className="grid grid-cols-3 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Action
								</label>
								<select
									value={action}
									onChange={(e) => {
										setAction(e.target.value);
										setPage(1);
									}}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-brand-500 focus:border-brand-500"
								>
									<option value="">All Actions</option>
									{(actions || []).map((a) => (
										<option key={a} value={a}>
											{a}
										</option>
									))}
								</select>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Start Date
								</label>
								<input
									type="date"
									value={startDate}
									onChange={(e) => {
										setStartDate(e.target.value);
										setPage(1);
									}}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-brand-500 focus:border-brand-500"
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									End Date
								</label>
								<input
									type="date"
									value={endDate}
									onChange={(e) => {
										setEndDate(e.target.value);
										setPage(1);
									}}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-brand-500 focus:border-brand-500"
								/>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Data Table */}
			<div className="border border-gray-200 rounded-lg overflow-hidden">
				<DataTable
					data={auditData?.rows || []}
					columns={columns}
					loading={isLoading}
					onRowClick={(row) => setSelectedEntry(row)}
					pagination
					serverSidePagination
					currentPage={page}
					totalItems={auditData?.count || 0}
					onPageChange={setPage}
					pageSize={25}
					emptyTitle="No audit entries found"
					emptyDescription="Try adjusting your filters"
					hoverable
					bordered={false}
				/>
			</div>

			{/* Audit Entry Detail Modal */}
			<StackableModal
				open={!!selectedEntry}
				onClose={() => setSelectedEntry(null)}
				title="Audit Entry Details"
				size="3xl"
			>
				{selectedEntry && (
					<div className="space-y-6">
						{/* Header Info */}
						<div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-200">
							<div>
								<p className="text-sm text-gray-500">Timestamp</p>
								<p className="font-medium">
									{moment(selectedEntry.createdAt).format(
										'MMM D, YYYY h:mm:ss A',
									)}
								</p>
							</div>
							<div>
								<p className="text-sm text-gray-500">Action</p>
								<Badge
									variant={actionVariants[selectedEntry.action] || 'muted'}
								>
									{selectedEntry.action}
								</Badge>
							</div>
							<div>
								<p className="text-sm text-gray-500">Entity Type</p>
								<p className="font-medium">
									{entityTypeLabels[selectedEntry.entityType] ||
										selectedEntry.entityType}
								</p>
							</div>
							<div>
								<p className="text-sm text-gray-500">Entity</p>
								<p className="font-medium">
									{selectedEntry.entityName || 'Unnamed'}
								</p>
								<p className="text-xs text-gray-400 font-mono">
									{selectedEntry.entityId}
								</p>
							</div>
							<div className="col-span-2">
								<p className="text-sm text-gray-500">Actor</p>
								{selectedEntry.actorName ? (
									<>
										<p className="font-medium">{selectedEntry.actorName}</p>
										<p className="text-sm text-gray-500">
											{selectedEntry.actorEmail}
										</p>
									</>
								) : (
									<p className="text-gray-400">System</p>
								)}
							</div>
						</div>

						{/* Changes */}
						<div>
							<h4 className="font-medium text-gray-900 mb-3">Changes</h4>
							{renderChanges(selectedEntry.changes)}
						</div>

						{/* Context */}
						{(selectedEntry.ipAddress ||
							selectedEntry.requestId ||
							(selectedEntry.metadata &&
								Object.keys(selectedEntry.metadata).length > 0)) && (
							<div>
								<h4 className="font-medium text-gray-900 mb-3">Context</h4>
								<div className="space-y-2 text-sm">
									{selectedEntry.ipAddress && (
										<div>
											<span className="text-gray-500">IP Address:</span>{' '}
											<span className="font-mono">
												{selectedEntry.ipAddress}
											</span>
										</div>
									)}
									{selectedEntry.requestId && (
										<div>
											<span className="text-gray-500">Request ID:</span>{' '}
											<span className="font-mono text-xs">
												{selectedEntry.requestId}
											</span>
										</div>
									)}
									{selectedEntry.metadata &&
										Object.keys(selectedEntry.metadata).length > 0 && (
											<div>
												<span className="text-gray-500">Metadata:</span>
												<pre className="mt-1 text-xs bg-gray-50 p-3 rounded border overflow-auto max-h-40">
													{JSON.stringify(selectedEntry.metadata, null, 2)}
												</pre>
											</div>
										)}
								</div>
							</div>
						)}

						{/* Previous/New State (collapsible) */}
						{(selectedEntry.previousState || selectedEntry.newState) && (
							<div>
								<h4 className="font-medium text-gray-900 mb-3">
									State Snapshots
								</h4>
								<div className="grid grid-cols-2 gap-4">
									{selectedEntry.previousState && (
										<div>
											<p className="text-sm text-gray-500 mb-1">
												Previous State
											</p>
											<pre className="text-xs bg-red-50 p-3 rounded border border-red-100 overflow-auto max-h-48">
												{JSON.stringify(selectedEntry.previousState, null, 2)}
											</pre>
										</div>
									)}
									{selectedEntry.newState && (
										<div>
											<p className="text-sm text-gray-500 mb-1">New State</p>
											<pre className="text-xs bg-green-50 p-3 rounded border border-green-100 overflow-auto max-h-48">
												{JSON.stringify(selectedEntry.newState, null, 2)}
											</pre>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				)}
			</StackableModal>
		</MainContent>
	);
};

export default AuditLog;
